import { format, differenceInDays } from 'date-fns';
import { nl } from 'date-fns/locale';
import { MapPin, User, CalendarBlank, Check, X, UsersThree } from '@phosphor-icons/react';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { FlowIndicator } from './FlowIndicator';
import { TerminationUnit } from '../hooks/useHerverhuurPipeline';

interface TerminationCardProps {
  unit: TerminationUnit;
  step: 1 | 2;
}

const formatDate = (date: string | null) => {
  if (!date) return '—';
  try {
    return format(new Date(date), 'd MMM yyyy', { locale: nl });
  } catch {
    return '—';
  }
};

const daysUntil = (date: string | null): number | null => {
  if (!date) return null;
  try {
    return differenceInDays(new Date(date), new Date());
  } catch {
    return null;
  }
};

export const TerminationCard = ({ unit, step }: TerminationCardProps) => {
  const daysLeft = daysUntil(unit.contractEndDate);

  return (
    <Card className="border border-border shadow-sm hover:shadow-md transition-shadow cursor-default">
      <CardContent className="p-3 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-1.5 min-w-0">
            <MapPin className="text-neutral-secondary-mid mt-0.5 flex-shrink-0" size={13} weight="fill" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-neutral-primary leading-tight truncate">
                {unit.buildingName || unit.buildingId}
              </p>
              <p className="text-[11px] text-neutral-secondary-mid">
                {unit.unitLabel} • {unit.floor !== null ? `Verd. ${unit.floor}` : '—'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {unit.activeCandidateCount > 0 && (
              <Badge
                variant="outline"
                className="text-[10px] h-5 px-1.5 border-blue-300 text-blue-700 bg-blue-50 gap-1"
              >
                <UsersThree size={10} weight="bold" />
                {unit.activeCandidateCount}
              </Badge>
            )}
            {daysLeft !== null && (
              <Badge
                variant="outline"
                className={`text-[10px] h-5 px-1.5 ${
                  daysLeft <= 0
                    ? 'border-red-300 text-red-600 bg-red-50'
                    : daysLeft <= 30
                    ? 'border-amber-300 text-amber-700 bg-amber-50'
                    : 'border-border text-neutral-secondary-mid'
                }`}
              >
                {daysLeft <= 0 ? 'Voorbij' : `${daysLeft}d`}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <User className="text-neutral-secondary-mid flex-shrink-0" size={13} />
          <p className="text-xs text-neutral-primary truncate">
            {unit.tenantName || <span className="text-neutral-secondary-mid italic">Onbekend</span>}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <CalendarBlank className="text-neutral-secondary-mid flex-shrink-0" size={13} />
          <p className="text-xs text-neutral-secondary-mid">
            Einde: <span className="text-neutral-primary">{formatDate(unit.contractEndDate)}</span>
          </p>
        </div>

        <div className="flex flex-col gap-1 pt-1 border-t border-border">
          <StatusRow
            label="Checklist verstuurd"
            checked={unit.outgoingChecklistSent}
          />
          {step === 2 && (
            <>
              <StatusRow
                label="Inspectiedatum"
                checked={!!unit.outgoingInspectionDate}
                value={unit.outgoingInspectionDate ? formatDate(unit.outgoingInspectionDate) : undefined}
              />
              <StatusRow label="Waarborg vrijgegeven" checked={unit.guaranteeReleased} />
            </>
          )}
        </div>

        <div className="flex items-center pt-1">
          <FlowIndicator activeStep={step} />
        </div>
      </CardContent>
    </Card>
  );
};

const StatusRow = ({ label, checked, value }: { label: string; checked: boolean; value?: string }) => (
  <div className="flex items-center gap-1.5">
    {checked ? (
      <Check className="text-moss-primary flex-shrink-0" size={12} weight="bold" />
    ) : (
      <X className="text-neutral-secondary-mid flex-shrink-0" size={12} weight="bold" />
    )}
    <span className="text-[11px] text-neutral-secondary-mid">
      {label}
      {value && <span className="text-neutral-primary ml-1">{value}</span>}
    </span>
  </div>
);
